//  Imported under its real name: despite the `use` prefix it is a factory, not a React hook.
import createApiClient from '../interceptor';
import { billingExportRefreshPath } from './constants';
import {
  IBillingExportRefreshView,
  TBillingExportRefreshParams,
  TBillingExportRefreshStatus,
} from './types';

const apiClient = createApiClient();

//  billing-service's BillingExportStatus. Anything else is an answer this side does not understand.
const STATUSES: TBillingExportRefreshStatus[] = ['NONE', 'GENERATING', 'READY', 'FAILED'];

const asText = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

/**
 * The closeout period's billing export refresh state (BOPS-397).
 *
 * Returns `null` for anything that is not a clean answer — a non-2xx (including the 404 of a
 * billing-service that predates #342, or a 400 on bad dates), a dropped connection, or a body
 * whose status is not one of the four. The caller treats `null` as "refresh state unknown" and falls back to the
 * pre-BOPS-397 screen, so an unclear answer can neither leave a spinner running forever nor
 * report a failure that did not happen.
 */
export const getBillingExportRefresh = async (
  params: TBillingExportRefreshParams
): Promise<IBillingExportRefreshView | null> => {
  try {
    const response = await apiClient.get(billingExportRefreshPath, { params });

    const httpStatus = Number(response?.status);
    //  The response interceptor resolves rather than rejects on everything but a 401, so the
    //  outcome is read off the status instead of a catch.
    if (!(httpStatus >= 200 && httpStatus < 300)) return null;

    const body = response?.data ?? {};
    const status = STATUSES.find((known) => known === body?.status);
    if (!status) return null;

    return {
      status,
      jobId: asText(body.jobId),
      refreshedAt: asText(body.refreshedAt),
      refreshedAtLabel: asText(body.refreshedAtLabel),
      timeZone: asText(body.timeZone),
    };
  } catch {
    return null;
  }
};
